


import { Component, Prop, Ref, Vue, Watch } from "vue-property-decorator";
import Chart from "chart.js/auto";

import { CustomError } from "@Internal/Models";
import { PagerSorter } from "App/Models";

import { Newsletters } from "App";
import { $t, EventBus } from "App/IOC";
import {
    Newsletter, NewsletterPostStats, NewsletterSend, NewsletterSendRecipients, NewsletterSummary
} from "App/Models/Newsletter";

import TablePagination from "Web/Components/Table/Pagination.vue";

import PeriodPicker from "./PeriodPicker.vue";

import { DemoData } from "./Demo";
import { ErrorMessage, FormatDate, FormatNumber, FormatRate, SendStatusLabel } from "./Helpers";
import { FUNNEL_STAGES, FunnelStage, StageColor, StagesOfPosts } from "./Stage";

/* LES STATISTIQUES D'UNE NEWSLETTER ( brief du 13/10, § 0 ; lot 4 ), sous le
   resume de `StatsPage.vue`, comme dans le Desktop : synthese sur la periode,
   courbe envoi apres envoi, tableau des envois ( un clic : ses contacts et ses
   articles ), classement des articles.

   ⚠️ Les ouvertures passent par le pixel : sous-comptees. Les clics sont le chiffre
   fiable ; les articles cliques arrivent le lendemain ( export SendGrid ). */
/* Une petite courbe sous la grande : son titre, son unite, ses valeurs. */
interface MiniChart {
    key: string;
    title: string;
    unit: string;
    value: string;
    detail: string;
    values: number[];
}

/* Le seuil d'ouvertures sous lequel une bulle est pale : trop peu pour conclure. */
const FEW_OPENS = 20;

/* Une etape de l'entonnoir « du destinataire au lecteur », comme le Desktop.
   `value` vaut `null` quand le back ne la donne pas encore ( visites du blog ). */
interface FunnelStep {
    key: string;
    label: string;
    value: number | null;
    unit: string;
    ratio: string;
    sub: string;
}

/* Les raccourcis de periode en tete ( captures Insyder, 22/10/2026 ). */
const QUICKS: { key: string; months: number }[] = [
    { key: "3m", months: 3 }, { key: "6m", months: 6 }, { key: "1y", months: 12 },
    { key: "all", months: 0 }
];

function pad2( value: number ): string {
    return String( value ).padStart( 2, "0" );
}

function isoDay( date: Date ): string {
    return `${ date.getFullYear() }-${ pad2( date.getMonth() + 1 ) }-${ pad2( date.getDate() ) }`;
}

@Component( {
    components: { PeriodPicker, TablePagination }
} )
export default class NewsletterStats extends Vue {
    @Prop( { required: true } )
    readonly GroupId!: number;

    @Prop( { required: true } )
    readonly Record!: Newsletter;

    /* Les donnees de demonstration ( `?demo=1`, hors production ) : a la place des
       appels au back quand elles sont la. */
    @Prop( { default: null } )
    readonly Demo!: DemoData | null;

    @Ref()
    readonly curve?: HTMLCanvasElement;

    @Ref()
    readonly bubbles?: HTMLCanvasElement;

    /* La periode, comme le Desktop ( Fred, 22/10/2026 ) : `[ debut, fin ]` en
       `YYYY-MM-DD`, vide = depuis le premier envoi. */
    private period: string[] = [];

    /* Le raccourci choisi : `3m`, `6m`, `1y`, `all`, ou `custom` ( calendrier ). */
    private quick: string = "all";

    private get from(): string {
        return this.period[ 0 ] || "";
    }

    private get to(): string {
        return this.period[ 1 ] || "";
    }

    private loading: boolean = false;
    private summary: NewsletterSummary | null = null;
    private sends: NewsletterSend[] = [];
    private posts: NewsletterPostStats[] = [];

    /* La pagination des deux tableaux, cote ecran ( Fred, 22/10/2026 ) : le back
       rend tous les envois et tous les articles d'un coup. Meme barre que les
       autres tableaux de la Console ( `Table/Pagination.vue` ). */
    private sendsPager: PagerSorter = new PagerSorter( 10 );
    private postsPager: PagerSorter = new PagerSorter( 10 );

    private opened: NewsletterSend | null = null;
    private recipients: NewsletterSendRecipients | null = null;

    /* Les contacts d'un envoi, page par page, comme le Desktop : le back rend 200
       contacts au plus par appel, un envoi en a des milliers. */
    private recipientsPager: PagerSorter = new PagerSorter( 25 );

    private chart: Chart | null = null;

    /* Les petites courbes sous la grande, une par tuile ( Fred, 22/10/2026 ) — comme
       le Desktop. */
    private minis: Chart[] = [];

    /* LES BULLES : quel article amene le plus d'activite, par etage de tunnel ( Fred,
       22/10/2026 ) — comme le Desktop. L'etage de chaque article ( `Stage.ts` ) et
       l'etage choisi ( `null` = tous ). */
    private stages: { [ id: number ]: FunnelStage } = {};
    private stageFilter: FunnelStage | null = null;
    private bubbleChart: Chart | null = null;
    private dark: boolean = false;

    /* Les couleurs des deux series, posees au dessin ( clair / sombre ) : la legende
       HTML les reprend. */
    private colors: { open: string; click: string; brand: string } = {
        open: "#2a78d6", click: "#eb6834", brand: "#ff6c00"
    };

    async mounted(): Promise<void> {
        await this.load();
    }

    beforeDestroy(): void {
        if( this.chart !== null )
            this.chart.destroy();

        this.minis.forEach( ( mini ): void => mini.destroy() );

        if( this.bubbleChart !== null )
            this.bubbleChart.destroy();
    }

    @Watch( "Demo" )
    private async onDemo(): Promise<void> {
        this.opened = null;
        this.recipients = null;
        await this.load();
    }

    private async load(): Promise<void> {
        this.loading = true;

        const id = this.Record.id;
        const from = this.from || undefined;
        const to = this.to || undefined;

        if( this.Demo !== null ) {
            this.loading = false;
            this.summary = this.Demo.summary( from, to );
            this.sends = this.Demo.sends;
            this.posts = this.Demo.posts( from, to );
            this.resetPagers();
            this.loadStages();
            this.$nextTick( (): void => this.draw() );
            return;
        }

        const [
            [ summary, summaryError ], [ sends, sendsError ], [ posts, postsError ]
        ] = await Promise.all( [
            Newsletters.Stats( this.GroupId, id, from, to ),
            Newsletters.Sends( this.GroupId, id ),
            Newsletters.PostsStats( this.GroupId, id, from, to )
        ] );

        this.loading = false;

        const error = summaryError || sendsError || postsError;

        if( error !== undefined ) {
            this.fail( error );
            return;
        }

        this.summary = summary || null;
        this.sends = sends || [];
        this.posts = posts || [];
        this.resetPagers();
        this.loadStages();

        this.$nextTick( () => this.draw() );
    }

    /* Les etages des articles du classement. En demonstration, un article sans tag
       d'etage en recoit un, fabrique. */
    private loadStages(): void {
        const stages = StagesOfPosts( this.posts );
        const demo = this.Demo;

        if( demo !== null )
            Object.keys( stages ).forEach( ( key: string ): void => {
                stages[ Number( key ) ] = demo.stage( Number( key ) );
            } );

        this.stages = stages;
    }

    private stageOf( id: number ): FunnelStage {
        return this.stages[ id ] || "none";
    }

    private stageName( stage: FunnelStage ): string {
        const keys: { [ key: string ]: string } = {
            top: "NEWSLETTERS_stageTop",
            middle: "NEWSLETTERS_stageMiddle",
            bottom: "NEWSLETTERS_stageBottom",
            none: "NEWSLETTERS_stageNone"
        };

        return $t( keys[ stage ] ).toString();
    }

    private stageColor( stage: FunnelStage ): string {
        return StageColor( stage, this.dark );
    }

    /* Les etages presents dans le classement, dans l'ordre du tunnel. */
    private get presentStages(): FunnelStage[] {
        return FUNNEL_STAGES.filter( ( stage: FunnelStage ): boolean =>
            this.posts.some( ( post ): boolean => this.stageOf( post.id ) === stage ) );
    }

    private get noStage(): boolean {
        return this.posts.length > 0 &&
            this.posts.every( ( post ): boolean => this.stageOf( post.id ) === "none" );
    }

    /* Les articles que le graphique peut placer : ceux dont le back donne les
       ouvertures. */
    private get bubblePosts(): NewsletterPostStats[] {
        return this.posts.filter( ( post ): boolean =>
            post.opened !== null && post.opened !== undefined && post.opened > 0 );
    }

    /* La taille ne parle que si le back donne les visites du blog. */
    private get bubbleSized(): boolean {
        return this.bubblePosts.some( ( post ): boolean =>
            post.blog_visits !== null && post.blog_visits !== undefined && post.blog_visits > 0 );
    }

    private pickStage( stage: FunnelStage | null ): void {
        this.stageFilter = stage;
        this.$nextTick( (): void => this.drawBubbles() );
    }

    /* Une bulle par article, comme le Desktop ( Fred, 22/10/2026 ) :
       - en largeur, les OUVERTURES : contacts qui ont ouvert un e-mail ou l'article
         figurait ( `opened`, demande au back le 22/10 ) ;
       - en hauteur, le TAUX DE CLIC DES LECTEURS : clics / ouvertures ;
       - la taille suit les visites du blog quand le back les donne, sinon une seule
         taille ;
       - sous 20 ouvertures, la bulle est pale : trop peu pour conclure. */
    private drawBubbles(): void {
        if( this.bubbleChart !== null ) {
            this.bubbleChart.destroy();
            this.bubbleChart = null;
        }

        const canvas = this.bubbles;
        const posts = this.bubblePosts;

        if( canvas === undefined || posts.length === 0 )
            return;

        const style = window.getComputedStyle( canvas );
        const token = ( name: string, fallback: string ): string =>
            style.getPropertyValue( name ).trim() || fallback;
        const dark = document.documentElement.getAttribute( "data-theme" ) === "dark";
        const surface = token( "--bg-surface", dark ? "#1a1a19" : "#ffffff" );
        const ink = token( "--fg-secondary", "#52514e" );
        const grid = token( "--border-default", "#e4e7eb" );
        const sized = this.bubbleSized;
        const maxVisits = Math.max( 1, ...posts.map( ( post ): number => post.blog_visits || 0 ) );

        this.dark = dark;

        const shown = FUNNEL_STAGES.filter( ( stage: FunnelStage ): boolean =>
            this.stageFilter === null || this.stageFilter === stage );

        const datasets = shown
            .map( ( stage: FunnelStage ) => {
                const rows = posts.filter( ( post ): boolean => this.stageOf( post.id ) === stage );
                const color = StageColor( stage, dark );

                return {
                    label: this.stageName( stage ),
                    rows,
                    data: rows.map( ( post ): { x: number; y: number; r: number } => ( {
                        x: post.opened as number,
                        y: Math.round( post.clicks / ( post.opened as number ) * 1000 ) / 10,
                        r: sized ? 5 + 15 * Math.sqrt( ( post.blog_visits || 0 ) / maxVisits ) : 7
                    } ) ),
                    backgroundColor: rows.map( ( post ): string =>
                        ( post.opened as number ) < FEW_OPENS ? `${ color }4d` : `${ color }cc` ),
                    hoverBackgroundColor: color,
                    borderColor: surface,
                    borderWidth: 1.5,
                    hoverBorderColor: surface,
                    hoverBorderWidth: 2
                };
            } )
            .filter( ( dataset ): boolean => dataset.rows.length > 0 );

        const postAt = ( item: any ): NewsletterPostStats =>
            datasets[ item.datasetIndex ].rows[ item.dataIndex ];

        this.bubbleChart = new Chart( canvas, {
            type: "bubble",
            data: { datasets },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                layout: { padding: { top: 16, right: 24, left: 8 } },
                interaction: { mode: "nearest", intersect: true },
                onHover: ( event: any, items: any[] ): void => {
                    canvas.style.cursor = items.length > 0 ? "pointer" : "default";
                },
                onClick: ( event: any, items: any[] ): void => {
                    if( items.length === 0 )
                        return;

                    const post = postAt( items[ 0 ] );

                    if( post !== undefined && post.url )
                        window.open( post.url, "_blank", "noopener" );
                },
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        backgroundColor: surface,
                        borderColor: grid,
                        borderWidth: 1,
                        titleColor: token( "--fg-primary", "#1e2a3a" ),
                        bodyColor: ink,
                        footerColor: token( "--fg-muted", "#8a8984" ),
                        footerFont: { weight: "normal" },
                        padding: 10,
                        caretSize: 0,
                        cornerRadius: 8,
                        displayColors: false,
                        callbacks: {
                            title: ( items: any[] ): string => postAt( items[ 0 ] ).title,
                            label: ( item: any ): string[] => {
                                const post = postAt( item );
                                const lines = [
                                    datasets[ item.datasetIndex ].label,
                                    $t( "NEWSLETTERS_bubbleOpened", {
                                        count: post.opened as number
                                    } ).toString(),
                                    $t( "NEWSLETTERS_bubbleClicks", {
                                        count: post.clicks, rate: this.rate( item.parsed.y )
                                    } ).toString()
                                ];

                                if( post.average_position !== null &&
                                    post.average_position !== undefined )
                                    lines.push( $t( "NEWSLETTERS_bubblePosition", {
                                        position: post.average_position.toFixed( 1 )
                                            .replace( ".0", "" ).replace( ".", "," )
                                    } ).toString() );

                                if( ( post.opened as number ) < FEW_OPENS )
                                    lines.push( $t( "NEWSLETTERS_bubbleFew", {
                                        count: FEW_OPENS
                                    } ).toString() );

                                if( post.blog_visits !== null && post.blog_visits !== undefined )
                                    lines.push( $t( "NEWSLETTERS_bubbleVisits", {
                                        count: post.blog_visits
                                    } ).toString() );

                                return lines;
                            },
                            footer: ( items: any[] ): string =>
                                postAt( items[ 0 ] ).url
                                    ? $t( "NEWSLETTERS_bubbleOpen" ).toString()
                                    : ""
                        }
                    }
                },
                scales: {
                    x: {
                        title: {
                            display: true,
                            text: $t( "NEWSLETTERS_bubbleX" ).toString(),
                            color: ink,
                            font: { size: 11 }
                        },
                        grid: { color: grid, drawTicks: false },
                        border: { display: false, dash: [ 4, 4 ] },
                        ticks: { maxTicksLimit: 6, padding: 8, color: ink, font: { size: 11 } }
                    },
                    y: {
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: $t( "NEWSLETTERS_bubbleY" ).toString(),
                            color: ink,
                            font: { size: 11 }
                        },
                        grid: { color: grid, drawTicks: false },
                        border: { display: false, dash: [ 4, 4 ] },
                        ticks: {
                            maxTicksLimit: 5,
                            padding: 8,
                            color: ink,
                            font: { size: 11 },
                            callback: ( value: any ): string => `${ value } %`
                        }
                    }
                }
            }
        } as any );
    }

    /* Une periode choisie recharge les chiffres. */
    private get quicks(): { key: string; label: string }[] {
        return QUICKS.map( ( quick ): { key: string; label: string } => ( {
            key: quick.key,
            label: $t( `NEWSLETTERS_quick${ quick.key === "all" ? "All" : quick.key }` ).toString()
        } ) );
    }

    /* Un raccourci : les N derniers mois jusqu'a aujourd'hui, ou tout. */
    private async pickQuick( key: string ): Promise<void> {
        const quick = QUICKS.find( ( item ): boolean => item.key === key );

        if( quick === undefined )
            return;

        const today = new Date();

        this.quick = key;
        this.period = quick.months === 0
            ? []
            : [
                isoDay( new Date(
                    today.getFullYear(), today.getMonth() - quick.months, today.getDate()
                ) ),
                isoDay( today )
            ];

        await this.load();
    }

    private async onPeriod( value: string[] ): Promise<void> {
        this.period = value;
        this.quick = value.length === 0 ? "all" : "custom";
        await this.load();
    }

    private resetPagers(): void {
        this.sendsPager = this.pager( this.sendsPager.Limit, 1, this.sends.length );
        this.postsPager = this.pager( this.postsPager.Limit, 1, this.posts.length );
    }

    private pager( limit: number, page: number, total: number ): PagerSorter {
        const pager = new PagerSorter( limit );

        /* ⚠️ `Pages` est un ACCESSEUR dans la Console ( calcule depuis `Total` et
           `Limit` ) : l'ecrire leve une erreur et coupait tout le chargement. */
        pager.Total = total;
        pager.Page = Math.min( Math.max( 1, page ), Math.max( 1, Math.ceil( total / limit ) ) );

        return pager;
    }

    private onSendsPager( pager: PagerSorter ): void {
        this.sendsPager = this.pager( pager.Limit, pager.Page, this.sends.length );
    }

    private onPostsPager( pager: PagerSorter ): void {
        this.postsPager = this.pager( pager.Limit, pager.Page, this.posts.length );
    }

    private get pagedSends(): NewsletterSend[] {
        const start = ( this.sendsPager.Page - 1 ) * this.sendsPager.Limit;

        return this.sends.slice( start, start + this.sendsPager.Limit );
    }

    private get pagedPosts(): NewsletterPostStats[] {
        const start = ( this.postsPager.Page - 1 ) * this.postsPager.Limit;

        return this.posts.slice( start, start + this.postsPager.Limit );
    }

    /* LA COURBE ( Fred, 22/10/2026 : « un graph plus moderne » ) — la meme que le
       Desktop ( `Newsletter/Automatic/Stats.vue` ), en chart.js 4.

       - UN SEUL AXE, en % : taux d'ouverture et taux de clic. Les desinscriptions
         ( un nombre, une autre echelle ) n'y sont plus — deux axes font lire des
         croisements qui n'existent pas ; elles restent dans la tuile et le tableau.
       - Palette validee ( dataviz, `validate_palette.js` ) : bleu #2a78d6 /
         orange #eb6834 en clair, #3987e5 / #d95926 en sombre.
       - Traits de 2 px, points caches sauf au survol, grille horizontale discrete.
       - Survol : ligne verticale et infobulle des deux taux de l'envoi.

       Encre, grille et fond LUS dans les variables du theme ( `--fg-*`, `--border-*`,
       `--bg-surface` ) : la courbe suit le theme clair / sombre. */
    private draw(): void {
        if( this.chart !== null ) {
            this.chart.destroy();
            this.chart = null;
        }

        this.drawMinis();
        this.drawBubbles();

        if( this.curve === undefined || this.summary === null || this.summary.curve.length === 0 )
            return;

        const points = this.summary.curve;
        const style = window.getComputedStyle( this.curve );
        const token = ( name: string, fallback: string ): string =>
            style.getPropertyValue( name ).trim() || fallback;
        const dark = document.documentElement.getAttribute( "data-theme" ) === "dark";
        const surface = token( "--bg-surface", dark ? "#1a1a19" : "#ffffff" );
        const ink = token( "--fg-secondary", "#52514e" );
        const grid = token( "--border-default", "#e4e7eb" );
        const open = dark ? "#3987e5" : "#2a78d6";
        const click = dark ? "#d95926" : "#eb6834";
        const faint = dark ? "rgba(255,255,255,.05)" : "rgba(15,23,42,.04)";

        this.colors = { open, click, brand: dark ? "#fd9a3b" : "#ff6c00" };

        /* Un voile sous chaque courbe, qui s'efface vers le bas. */
        const veil = ( rgb: string, alpha: number ): ( context: any ) => CanvasGradient | string =>
            ( context: any ): CanvasGradient | string => {
                const area = context.chart.chartArea;

                if( !area )
                    return "transparent";

                const gradient = context.chart.ctx.createLinearGradient(
                    0, area.top, 0, area.bottom
                );

                gradient.addColorStop( 0, `rgba(${ rgb },${ alpha })` );
                gradient.addColorStop( 1, `rgba(${ rgb },0)` );

                return gradient;
            };

        const line = ( color: string ): { [ key: string ]: unknown } => ( {
            borderColor: color,
            borderWidth: 2,
            /* Un seul envoi = une courbe d'un seul point : on le montre ( 28/10/2026 ). */
            pointRadius: ( points.length === 1 ) ? 4 : 0,
            pointHoverRadius: 5,
            pointHitRadius: 12,
            pointBackgroundColor: color,
            pointHoverBorderColor: surface,
            pointHoverBorderWidth: 2,
            tension: .3
        } );

        /* La ligne verticale du survol. */
        const crosshair = {
            id: "nlCrosshair",
            afterDatasetsDraw: ( chart: any ): void => {
                const active = chart.tooltip ? chart.tooltip.getActiveElements() : [];

                if( active.length === 0 )
                    return;

                const x = active[ 0 ].element.x;
                const area = chart.chartArea;
                const ctx = chart.ctx;

                ctx.save();
                ctx.beginPath();
                ctx.moveTo( x, area.top );
                ctx.lineTo( x, area.bottom );
                ctx.lineWidth = 1;
                ctx.strokeStyle = grid;
                ctx.stroke();
                ctx.restore();
            }
        };

        this.chart = new Chart( this.curve, {
            type: "line",
            plugins: [ crosshair ],
            data: {
                labels: points.map( ( point ): string => this.shortDate( point.date ) ),
                datasets: [
                    {
                        label: $t( "NEWSLETTERS_openRate" ).toString(),
                        data: points.map( ( point ): number | null => point.open_rate ),
                        backgroundColor: veil(
                            dark ? "57,135,229" : "42,120,214", dark ? .24 : .16
                        ),
                        fill: true,
                        ...line( open )
                    },
                    {
                        label: $t( "NEWSLETTERS_clickRate" ).toString(),
                        data: points.map( ( point ): number | null => point.click_rate ),
                        backgroundColor: veil( dark ? "217,89,38" : "235,104,52", dark ? .18 : .1 ),
                        fill: true,
                        ...line( click )
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                layout: { padding: { top: 8, right: 8 } },
                interaction: { mode: "index", intersect: false },
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        backgroundColor: surface,
                        borderColor: grid,
                        borderWidth: 1,
                        titleColor: token( "--fg-primary", "#1e2a3a" ),
                        bodyColor: ink,
                        padding: 10,
                        caretSize: 0,
                        cornerRadius: 8,
                        usePointStyle: true,
                        boxWidth: 8,
                        boxHeight: 8,
                        callbacks: {
                            title: ( items: any[] ): string =>
                                FormatDate( points[ items[ 0 ].dataIndex ].date, false ),
                            label: ( item: any ): string =>
                                `${ item.dataset.label } : ${ this.rate( item.parsed.y ) }`
                        }
                    }
                },
                scales: {
                    x: {
                        // Un envoi unique : son point au milieu, pas colle a l'axe.
                        offset: points.length === 1,
                        grid: { color: faint, drawTicks: false },
                        border: { display: false },
                        ticks: {
                            color: ink,
                            font: { size: 11 },
                            maxRotation: 0,
                            autoSkipPadding: 16,
                            padding: 8
                        }
                    },
                    y: {
                        beginAtZero: true,
                        grid: { color: grid, drawTicks: false },
                        border: { display: false, dash: [ 4, 4 ] },
                        ticks: {
                            maxTicksLimit: 5,
                            padding: 8,
                            color: ink,
                            font: { size: 11 },
                            callback: ( value: any ): string => `${ value } %`
                        }
                    }
                }
            }
        } as any );
    }

    /* Les envois faits de la periode, du plus ancien au plus recent. Le back rend les
       envois sans filtre de date : on applique ici la periode choisie. */
    /* L'ENTONNOIR ( capture Insyder « Funnel de perte » ), comme le Desktop. */
    private get funnelSteps(): FunnelStep[] {
        const summary = this.summary;

        if( summary === null )
            return [];

        const emails = $t( "NEWSLETTERS_funnelUnitEmails" ).toString();
        const contacts = $t( "NEWSLETTERS_funnelUnitContacts" ).toString();
        const raw: [ string, number | null, string ][] = [
            [ "Sent", summary.emails.sent, emails ],
            [ "Delivered", summary.emails.delivered, emails ],
            [ "Opened", summary.opens.unique, contacts ],
            [ "Clicked", summary.clicks.unique, contacts ],
            [ "Visits", summary.blog_visits, $t( "NEWSLETTERS_funnelUnitVisits" ).toString() ]
        ];

        return raw.map( ( [ key, value, unit ], index ): FunnelStep => {
            const previous = index > 0 ? raw[ index - 1 ] : null;
            const ratio = ( previous === null )
                ? "100 %"
                : ( value !== null && previous[ 1 ] )
                    ? this.rate( value / ( previous[ 1 ] as number ) * 100 )
                    : "";

            return {
                key,
                label: $t( `NEWSLETTERS_funnel${ key }` ).toString(),
                value,
                unit,
                ratio,
                sub: previous === null
                    ? $t( "NEWSLETTERS_funnelStart" ).toString()
                    : $t( `NEWSLETTERS_funnelOf${ previous[ 0 ] }` ).toString()
            };
        } );
    }

    /* Le trace de l'entonnoir dans un cadre de 1000 x 160 ( voir le Desktop ). */
    private get funnelShape(): { band: string; soon: string } {
        const steps = this.funnelSteps;
        const width = 1000 / Math.max( 1, steps.length );
        const first = steps.length > 0 ? ( steps[ 0 ].value || 0 ) : 0;
        const known = steps.filter( ( step ): boolean => step.value !== null && first > 0 );

        if( known.length === 0 )
            return { band: "", soon: "" };

        const halves = known.map( ( step ): number =>
            Math.max( 6, 70 * Math.pow( ( step.value as number ) / first, 0.35 ) ) );
        const ease = width * 0.3;
        const top: string[] = [ `M 0 ${ 80 - halves[ 0 ] }` ];
        const bottom: string[] = [];

        halves.forEach( ( value, index ): void => {
            const start = index * width + ( index === 0 ? 0 : ease );
            const end = ( index + 1 ) * width - ( index === halves.length - 1 ? 0 : ease );

            if( index > 0 ) {
                const from = index * width - ease;
                const middle = index * width;

                top.push( `C ${ middle } ${ 80 - halves[ index - 1 ] } ${ middle } ` +
                    `${ 80 - value } ${ start } ${ 80 - value }` );
                bottom.unshift( `C ${ middle } ${ 80 + value } ${ middle } ` +
                    `${ 80 + halves[ index - 1 ] } ${ from } ${ 80 + halves[ index - 1 ] }` );
            }

            top.push( `L ${ end } ${ 80 - value }` );
            bottom.unshift( `L ${ index === 0 ? 0 : start } ${ 80 + value }` );
        } );

        const last = halves[ halves.length - 1 ];
        const right = known.length * width;
        const band = [ ...top, `L ${ right } ${ 80 + last }`, ...bottom, "Z" ].join( " " );
        const soon = ( known.length < steps.length )
            ? `M ${ right } ${ 80 - last } L 1000 ${ 80 - last } L 1000 ${ 80 + last } ` +
              `L ${ right } ${ 80 + last } Z`
            : "";

        return { band, soon };
    }

    private get funnelColumns(): string {
        return `repeat( ${ this.funnelSteps.length }, 1fr )`;
    }

    private get patternId(): string {
        return `nl-funnel-${ ( this as any )._uid }`;
    }

    private num( value: number | null | undefined ): string {
        return FormatNumber( value );
    }

    /* La barre d'un taux dans un tableau : sa longueur relative au plus fort. */
    private bar( value: number | null | undefined, max: number ): string {
        return `${ Math.max( 2, Math.min( 100, ( value || 0 ) / Math.max( max, 0.1 ) * 100 ) ) }%`;
    }

    private get maxOpenRate(): number {
        return Math.max( 0, ...this.sends.map( ( send ): number =>
            send.metrics ? send.metrics.open_rate || 0 : 0 ) );
    }

    private get maxClickRate(): number {
        return Math.max( 0, ...this.sends.map( ( send ): number =>
            send.metrics ? send.metrics.click_rate || 0 : 0 ) );
    }

    /* Le taux de clic des lecteurs d'un article ( clics / ouvertures ). */
    private ctor( post: NewsletterPostStats ): number | null {
        return ( post.opened !== null && post.opened !== undefined && post.opened > 0 )
            ? post.clicks / post.opened * 100
            : null;
    }

    private get maxCtor(): number {
        return Math.max( 0, ...this.posts.map( ( post ): number => this.ctor( post ) || 0 ) );
    }

    private get doneSends(): NewsletterSend[] {
        const from = this.from || undefined;
        const to = this.to || undefined;

        return this.sends
            .filter( ( send ): boolean => send.metrics !== null )
            .filter( ( send ): boolean => from === undefined || send.date.slice( 0, 10 ) >= from )
            .filter( ( send ): boolean => to === undefined || send.date.slice( 0, 10 ) <= to )
            .slice()
            .sort( ( a, b ): number => a.date.localeCompare( b.date ) );
    }

    /* Une petite courbe par tuile : le nombre de l'envoi, envoi apres envoi. */
    private get miniCharts(): MiniChart[] {
        const sends = this.doneSends;
        const summary = this.summary;
        const pick = ( read: ( send: NewsletterSend ) => number ): number[] =>
            sends.map( read );

        return [
            {
                key: "recipients",
                value: summary !== null ? this.num( summary.sends.done ) : "—",
                detail: summary === null
                    ? ""
                    : $t( summary.sends.skipped_no_article === 1
                        ? "NEWSLETTERS_sendsSkippedOne"
                        : "NEWSLETTERS_sendsSkipped", {
                        count: summary.sends.skipped_no_article
                    } ).toString(),
                title: $t( "NEWSLETTERS_sendsDone" ).toString(),
                unit: $t( "NEWSLETTERS_miniRecipients" ).toString(),
                values: pick( ( send ): number => send.recipients_count )
            },
            {
                key: "delivered",
                value: summary !== null ? this.num( summary.emails.sent ) : "—",
                detail: summary !== null ? $t( "NEWSLETTERS_deliveredBounced", {
                    delivered: this.num( summary.emails.delivered ),
                    bounced: this.num( summary.emails.bounced )
                } ).toString() : "",
                title: $t( "NEWSLETTERS_emailsSent" ).toString(),
                unit: $t( "NEWSLETTERS_miniDelivered" ).toString(),
                values: pick( ( send ): number => send.metrics!.delivered )
            },
            {
                key: "opened",
                value: summary !== null ? this.rate( summary.opens.rate ) : "—",
                detail: summary === null
                    ? ""
                    : $t( "NEWSLETTERS_uniqueUnder", {
                        count: this.num( summary.opens.unique )
                    } ).toString(),
                title: $t( "NEWSLETTERS_opens" ).toString(),
                unit: $t( "NEWSLETTERS_miniOpened" ).toString(),
                values: pick( ( send ): number => send.metrics!.opened )
            },
            {
                key: "clicked",
                value: summary !== null ? this.rate( summary.clicks.rate ) : "—",
                detail: summary !== null ? $t( "NEWSLETTERS_uniqueCto", {
                    count: this.num( summary.clicks.unique ),
                    cto: this.rate( summary.clicks.click_to_open )
                } ).toString() : "",
                title: $t( "NEWSLETTERS_clicks" ).toString(),
                unit: $t( "NEWSLETTERS_miniClicked" ).toString(),
                values: pick( ( send ): number => send.metrics!.clicked )
            },
            {
                key: "unsubscribed",
                value: summary !== null ? this.num( summary.unsubscribed ) : "—",
                detail: summary === null
                    ? ""
                    : $t( summary.spam_reports === 1 ? "NEWSLETTERS_spamOne" : "NEWSLETTERS_spam", {
                        count: summary.spam_reports
                    } ).toString(),
                title: $t( "NEWSLETTERS_unsubscribed" ).toString(),
                unit: $t( "NEWSLETTERS_miniUnsubscribed" ).toString(),
                values: pick( ( send ): number => send.metrics!.unsubscribed )
            }
        ];
    }

    /* Les petites courbes : une serie, la couleur des ouvertures, pas de legende
       ( le titre la nomme ), trois graduations, la meme infobulle. */
    /* ⚠️ L'INFOBULLE D'UNE PETITE COURBE EST EN HTML ( Fred, 28/10/2026 : au survol de
       « Envois faits », la date sortait coupee, la valeur absente ). Dessinee dans le
       canevas de 3,5 rem, elle n'y tenait pas. Posee au-dessus du point, elle deborde
       librement sur la carte. Texte pose en `textContent` : rien n'est interprete. */
    private placeMiniTip(
        canvas: HTMLCanvasElement, lines: string[] | null, x: number, y: number
    ): void {
        const box = canvas.parentElement;

        if( box === null )
            return;

        let tip = box.querySelector( ".c-newsletter-stats__mini-tip" ) as HTMLElement | null;

        if( tip === null ) {
            tip = document.createElement( "div" );
            tip.className = "c-newsletter-stats__mini-tip";
            box.appendChild( tip );
        }

        if( lines === null || lines.length === 0 ) {
            tip.style.opacity = "0";
            return;
        }

        const target = tip;

        target.textContent = "";
        lines.forEach( ( line: string, index: number ): void => {
            const part = document.createElement( index === 0 ? "strong" : "span" );

            part.textContent = line;
            target.appendChild( part );
        } );

        const half = target.offsetWidth / 2;

        target.style.left = `${ Math.min( Math.max( x, half ), box.clientWidth - half ) }px`;
        target.style.top = `${ y }px`;
        target.style.opacity = "1";
    }

    private drawMinis(): void {
        this.minis.forEach( ( mini ): void => mini.destroy() );
        this.minis = [];

        const canvases = ( this.$refs.mini || [] ) as HTMLCanvasElement[];
        const sends = this.doneSends;

        if( canvases.length === 0 || sends.length < 2 )
            return;

        const labels = sends.map( ( send ): string => this.shortDate( send.date ) );
        const style = window.getComputedStyle( canvases[ 0 ] );
        const token = ( name: string, fallback: string ): string =>
            style.getPropertyValue( name ).trim() || fallback;
        const dark = document.documentElement.getAttribute( "data-theme" ) === "dark";
        const surface = token( "--bg-surface", dark ? "#1a1a19" : "#ffffff" );
        /* L'orange de la marque ( Fred, 22/10/2026 : « dans nos couleurs oranges pour
           ces blocs » ) : une seule serie par carte, rien a distinguer. */
        const color = dark ? "#fd9a3b" : "#ff6c00";
        const rgb = dark ? "253,154,59" : "255,108,0";
        const veil = ( context: any ): CanvasGradient | string => {
            const area = context.chart.chartArea;

            if( !area )
                return "transparent";

            const gradient = context.chart.ctx.createLinearGradient(
                0, area.top, 0, area.bottom
            );

            gradient.addColorStop( 0, `rgba(${ rgb },${ dark ? .24 : .16 })` );
            gradient.addColorStop( 1, `rgba(${ rgb },0)` );

            return gradient;
        };

        this.miniCharts.forEach( ( mini, index ): void => {
            const canvas = canvases[ index ];

            if( canvas === undefined )
                return;

            this.minis.push( new Chart( canvas, {
                type: "line",
                data: {
                    labels,
                    datasets: [ {
                        label: mini.title,
                        data: mini.values,
                        backgroundColor: veil,
                        fill: true,
                        borderColor: color,
                        borderWidth: 2,
                        pointRadius: 0,
                        pointHoverRadius: 4,
                        pointHitRadius: 10,
                        pointBackgroundColor: color,
                        pointHoverBorderColor: surface,
                        pointHoverBorderWidth: 2,
                        tension: .3
                    } ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: { padding: { top: 6, bottom: 2, left: 2, right: 2 } },
                    interaction: { mode: "index", intersect: false },
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            enabled: false,
                            external: ( { tooltip }: any ): void => this.placeMiniTip(
                                canvas,
                                ( tooltip.opacity === 0 ) ? null : ( tooltip.title || [] ).concat(
                                    ...( tooltip.body || [] ).map( ( part: any ) => part.lines ) ),
                                tooltip.caretX, tooltip.caretY
                            ),
                            callbacks: {
                                title: ( items: any[] ): string =>
                                    FormatDate( sends[ items[ 0 ].dataIndex ].date, false ),
                                label: ( item: any ): string =>
                                    `${ this.num( item.parsed.y ) } ${ mini.unit }`
                            }
                        }
                    },
                    /* Une courbe nue, comme les cartes Insyder : sans axe ni grille,
                       l'echelle part du plus bas releve. */
                    scales: {
                        x: { display: false },
                        y: { display: false, beginAtZero: false }
                    }
                }
            } as any ) );
        } );
    }

    /* « 12 oct. » sous la courbe ; l'annee est dans l'infobulle. */
    private shortDate( value: string ): string {
        const date = new Date( value );

        return isNaN( date.getTime() )
            ? value
            : date.toLocaleDateString( "fr-FR", { day: "numeric", month: "short" } );
    }

    private async open( send: NewsletterSend ): Promise<void> {
        if( this.opened !== null && this.opened.id === send.id ) {
            this.opened = null;
            this.recipients = null;
            return;
        }

        this.opened = send;
        this.recipients = null;

        await this.loadRecipients( 1, this.recipientsPager.Limit );
    }

    /* Une page des contacts de l'envoi ouvert. */
    private async loadRecipients( page: number, limit: number ): Promise<void> {
        const send = this.opened;

        if( send === null )
            return;

        let recipients: NewsletterSendRecipients | null = null;

        if( this.Demo !== null )
            recipients = this.Demo.recipients( send.id, page, limit );
        else {
            const [ result, error ] = await Newsletters.SendRecipients(
                this.GroupId, this.Record.id, send.id, page, limit
            );

            if( error !== undefined ) {
                this.fail( error );
                return;
            }

            recipients = result || null;
        }

        /* L'envoi a pu etre referme pendant l'appel. */
        if( this.opened === null || this.opened.id !== send.id )
            return;

        this.recipients = recipients;
        this.recipientsPager = this.pager(
            limit, page, recipients !== null ? recipients.total : 0
        );
    }

    private async onRecipientsPager( pager: PagerSorter ): Promise<void> {
        await this.loadRecipients( pager.Page, pager.Limit );
    }

    private postTitle( id: number ): string {
        if( this.recipients === null )
            return `#${ id }`;

        const post = this.recipients.posts.find( item => item.id === id );

        return ( post !== undefined ) ? post.title : `#${ id }`;
    }

    private isOpened( send: NewsletterSend ): boolean {
        return this.opened !== null && this.opened.id === send.id;
    }

    private titles( ids: number[] ): string {
        return ids.map( this.postTitle ).join( ", " );
    }

    private openedLabel( opened: boolean ): string {
        return opened ? $t( "NEWSLETTERS_opened" ).toString() : "";
    }

    private fail( error: CustomError ): void {
        EventBus.Publish(
            "Error", new CustomError( error.Id, ErrorMessage( error.Message, error.SubId ) )
        );
    }

    private date( value: string | null, withTime: boolean = true ): string {
        return FormatDate( value, withTime );
    }

    private rate( value: number | null | undefined ): string {
        return FormatRate( value );
    }

    private status( send: NewsletterSend ): string {
        return SendStatusLabel( send );
    }

    private name(
        contact: { firstname: string | null; lastname: string | null; email: string | null }
    ): string {
        const full = [ contact.firstname, contact.lastname ].filter( part => !!part ).join( " " );

        return full || contact.email || "—";
    }
}
